import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faChalkboardTeacher,
    faStar,
    faGraduationCap,
    faArrowLeft,
    faSearch,
    faBookOpen,
    faThLarge,
    faFilter
} from '@fortawesome/free-solid-svg-icons';
import ApiServices from '../Services/ApiServices';
import Pagination from '../Shared/Pagination';
import Loader from '../Shared/Loader';
import Header from '../Shared/Header';
import Footer from '../Shared/Footer';
import { getFullFileUrl } from '../../Helpers/UrlHelpers';
import { toastHelper } from '../../Helpers/toastHelper';

const fadeUp = {
    hidden: { opacity: 0, y: 32 },
    visible: (i = 0) => ({
        opacity: 1,
        y: 0,
        transition: { duration: 0.65, delay: i * 0.12, ease: [0.22, 1, 0.36, 1] },
    }),
};

const PlatformTeachers = () => {
    const navigate = useNavigate();
    const [teachers, setTeachers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [pagination, setPagination] = useState({ currentPage: 1, totalPages: 1 });
    const [pageSize, setPageSize] = useState(6);

    const fetchTeachers = async (page = 1, query = '') => {
        setLoading(true);
        try {
            let url = `Teachers/getAll?PageSize=${pageSize}&PageNumber=${page}&IsActive=true`;
            if (query) url += `&Word=${encodeURIComponent(query)}`;
            const response = await ApiServices.get(url);
            setTeachers(response.data.items || []);
            setPagination({
                currentPage: response.data.current_page || page,
                totalPages: response.data.total_pages || 1
            });
        } catch (error) {
            toastHelper.error('حدث خطأ أثناء تحميل بيانات المعلمين');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchTeachers(1, searchQuery);
    }, [searchQuery, pageSize]);

    const handlePageChange = (page) => {
        fetchTeachers(page, searchQuery);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    return (
        <div className="pt-root" dir="rtl">
            <Header />

            {/* Page Header */}
            <section className="pt-hero">
                <div className="pt-container">
                    <motion.div className="pt-header" initial="hidden" whileInView="visible" viewport={{ once: true }}>
                        <motion.div className="pt-eyebrow" variants={fadeUp} custom={0}>
                            <span className="pt-eyebrow-dot" />
                            نخبة الكوادر التعليمية
                        </motion.div>
                        <motion.h2 className="pt-title" variants={fadeUp} custom={1}>
                            اكتشف أفضل <em>المعلمين</em>
                        </motion.h2>
                        <motion.p className="pt-subtitle" variants={fadeUp} custom={2}>
                            تصفح قائمة معلمينا المتميزين واختر الشريك الأمثل لرحلتك التعليمية المتميزة.
                        </motion.p>

                        <motion.div className="pt-controls" variants={fadeUp} custom={3}>
                            <div className="pt-search-box">
                                <FontAwesomeIcon icon={faSearch} className="pt-search-icon" />
                                <input
                                    type="text" placeholder="ابحث عن معلم أو مادة..."
                                    value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
                                />
                            </div>
                            <div className="pt-filter-box">
                                <div className="pt-filter-lbl">
                                    <FontAwesomeIcon icon={faThLarge} />
                                    <span>عرض:</span>
                                </div>
                                <select value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))}>
                                    {[6, 12, 24, 48].map(s => <option key={s} value={s}>{s}</option>)}
                                </select>
                            </div>
                        </motion.div>
                    </motion.div>
                </div>
            </section>

            <div className="pt-container">
                <div className="pt-main">
                    {loading ? (
                        <div className="pt-loader-wrap"><Loader /></div>
                    ) : (
                        <>
                            {teachers.length > 0 ? (
                                <div className="pt-grid">
                                    {teachers.map((t, i) => (
                                        <motion.div
                                            key={t.id} className="pt-card"
                                            variants={fadeUp} initial="hidden" animate="visible" custom={i % 6}
                                            whileHover={{ y: -8 }}
                                            onClick={() => navigate(`/teacher-profile/${t.id}`)}
                                        >
                                            <div className="pt-card-top">
                                                <img
                                                    src={getFullFileUrl(t.image)} alt={t.full_name}
                                                />
                                                <div className="pt-rate-badge">
                                                    <FontAwesomeIcon icon={faStar} />
                                                    <span>{t.rate || 0}</span>
                                                </div>
                                            </div>
                                            <div className="pt-card-body">
                                                <h3 className="pt-name">{t.name}</h3>
                                                <div className="pt-meta">
                                                    <div className="pt-meta-item is-qual">
                                                        <FontAwesomeIcon icon={faGraduationCap} />
                                                        <span>{t.qualification || 'معلم معتمد'}</span>
                                                    </div>
                                                    <div className="pt-meta-item is-mat">
                                                        <FontAwesomeIcon icon={faBookOpen} />
                                                        <span>{t.material_name || t.material || "جميع المواد"}</span>
                                                    </div>
                                                </div>
                                                <p className="pt-desc">
                                                    {t.about ? (t.about.length > 90 ? t.about.substring(0, 90) + '...' : t.about) : 'معلم متميز متاح حالياً لمساعدتك في رحلتك الدراسية.'}
                                                </p>
                                                <button className="pt-btn-profile">
                                                    <span>عرض الملف الشخصي</span>
                                                    <FontAwesomeIcon icon={faArrowLeft} />
                                                </button>
                                            </div>
                                        </motion.div>
                                    ))}
                                </div>
                            ) : (
                                <div className="pt-empty">
                                    <FontAwesomeIcon icon={faSearch} />
                                    <p>عذراً، لم نجد نتائج مطابقة لبحثك</p>
                                    <button onClick={() => setSearchQuery('')}>إعادة ضبط البحث</button>
                                </div>
                            )}

                            <div className="pt-pagination">
                                <Pagination
                                    currentPage={pagination.currentPage}
                                    totalPages={pagination.totalPages}
                                    onPageChange={handlePageChange}
                                />
                            </div>
                        </>
                    )}
                </div>
            </div>

            <Footer />

            <style jsx>{`
                .pt-root {
                    font-family: "Zain", sans-serif;
                    background: #fff;
                    min-height: 100vh;
                    direction: rtl;
                }

                .pt-container {
                    max-width: 1300px;
                    margin: 0 auto;
                    padding: 0 24px;
                    width: 100%;
                    box-sizing: border-box;
                }

                /* ── Header ── */
                .pt-hero { padding: 160px 0 60px; background: #fcfcfd; border-bottom: 1px solid #f1f5f9; }
                .pt-header { text-align: center; }
                .pt-eyebrow {
                    display: inline-flex; align-items: center; gap: 10px;
                    color: #6366f1; font-weight: 800; font-size: 0.85rem;
                    letter-spacing: 0.14em; text-transform: uppercase;
                    margin-bottom: 24px;
                }
                .pt-eyebrow-dot {
                    width: 8px; height: 8px; border-radius: 50%; background: #6366f1;
                    animation: pt-pulse 2.2s ease-in-out infinite;
                }
                @keyframes pt-pulse {
                    0%,100%{ box-shadow: 0 0 0 0 rgba(232,146,10,0.5); }
                    50%{ box-shadow: 0 0 0 8px rgba(232,146,10,0); }
                }
                .pt-title {
                    font-size: clamp(2.4rem, 4.5vw, 4rem);
                    font-weight: 900; color: #0f172a; margin: 0 0 20px;
                    line-height: 1.18; letter-spacing: -0.025em;
                }
                .pt-title em {
                    font-style: normal;
                    background: linear-gradient(90deg, #6366f1 0%, #6366f1 60%, #6366f1 100%);
                    -webkit-background-clip: text; -webkit-text-fill-color: transparent;
                    background-clip: text;
                }
                .pt-subtitle {
                    font-size: 1.25rem; color: #64748b; line-height: 1.85; margin: 0 auto 48px;
                    max-width: 700px; font-weight: 500; padding: 0 16px;
                }

                /* ── Controls ── */
                .pt-controls {
                    display: flex; align-items: center; gap: 16px; max-width: 800px; margin: 0 auto;
                    width: 100%; box-sizing: border-box; padding: 0 16px;
                }
                .pt-search-box {
                    flex: 1; position: relative; min-width: 0; width: 100%;
                }
                .pt-search-icon {
                    position: absolute; right: 24px; top: 50%; transform: translateY(-50%);
                    color: #94a3b8; font-size: 1.1rem;
                }
                .pt-search-box input {
                    width: 100%; height: 64px; padding: 0 60px 0 24px;
                    background: #fff; border: 1.5px solid #f1f5f9; border-radius: 50px;
                    font-family: inherit; font-size: 1.15rem; font-weight: 700;
                    box-shadow: 0 10px 30px rgba(0,0,0,0.03); outline: none; transition: 0.3s;
                    box-sizing: border-box;
                }
                .pt-search-box input:focus { border-color: #6366f1; box-shadow: 0 10px 30px rgba(99, 102, 241, 0.08); }

                .pt-filter-box {
                    display: flex; align-items: center; gap: 12px; height: 64px;
                    padding: 0 24px; background: #fff; border: 1.5px solid #f1f5f9;
                    border-radius: 50px; box-shadow: 0 10px 30px rgba(0,0,0,0.03);
                    box-sizing: border-box; width: auto;
                }
                .pt-filter-lbl { display: flex; align-items: center; gap: 8px; color: #94a3b8; font-weight: 800; font-size: 0.95rem; white-space: nowrap; }
                .pt-filter-box select {
                    border: none; background: transparent; font-family: inherit;
                    font-weight: 900; color: #1e293b; font-size: 1.1rem; outline: none; cursor: pointer;
                }

                /* ── Main Content ── */
                .pt-main { padding: 80px 0 120px; }
                .pt-grid {
                    display: grid; grid-template-columns: repeat(auto-fill, minmax(300px, 1fr)); gap: 32px;
                }

                /* ── Card ── */
                .pt-card {
                    background: #fff; border: 1.5px solid rgba(15,23,42,0.06);
                    border-radius: 32px; overflow: hidden; cursor: pointer; transition: 0.3s;
                    display: flex; flex-direction: column; height: 100%;
                }
                .pt-card:hover { border-color: rgba(15,23,42,0.12); box-shadow: 0 20px 40px rgba(0,0,0,0.03); }

                .pt-card-top { position: relative; height: 260px; overflow: hidden; flex-shrink: 0; }
                .pt-card-top img { width: 100%; height: 100%; object-fit: cover; transition: 0.5s; }
                .pt-card:hover .pt-card-top img { transform: scale(1.08); }
                .pt-rate-badge {
                    position: absolute; top: 16px; right: 16px; background: #fff;
                    padding: 6px 14px; border-radius: 12px; display: flex; align-items: center;
                    gap: 6px; font-weight: 900; color: #6366f1; box-shadow: 0 8px 20px rgba(0,0,0,0.1);
                }

                .pt-card-body { padding: 32px; flex: 1; display: flex; flex-direction: column; }
                .pt-name { font-size: 1.6rem; font-weight: 900; color: #0f172a; margin-bottom: 12px; }
                .pt-meta { display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 20px; }
                .pt-meta-item { display: flex; align-items: center; gap: 8px; padding: 6px 14px; border-radius: 10px; font-weight: 800; font-size: 0.85rem; }
                .pt-meta-item.is-qual { background: #f5f3ff; color: #6366f1; }
                .pt-meta-item.is-mat { background: #ecfdf5; color: #10b981; }

                .pt-desc { font-size: 1.05rem; color: #64748b; line-height: 1.7; font-weight: 500; margin-bottom: 28px; min-height: 3.4em; }

                .pt-btn-profile {
                    width: 100%; height: 52px; background: #f8fafc; border: 1.5px solid #f1f5f9;
                    border-radius: 16px; display: flex; align-items: center; justify-content: center;
                    gap: 10px; color: #1e293b; font-family: inherit; font-weight: 900; transition: 0.3s;
                    margin-top: auto;
                }
                .pt-card:hover .pt-btn-profile { background: #6366f1; color: #fff; border-color: #6366f1; }

                /* ── Empty State ── */
                .pt-empty { text-align: center; padding: 100px 0; color: #94a3b8; }
                .pt-empty svg { font-size: 4rem; opacity: 0.2; margin-bottom: 24px; }
                .pt-empty p { font-size: 1.4rem; font-weight: 800; margin-bottom: 20px; }
                .pt-empty button { padding: 12px 32px; background: #6366f1; color: #fff; border: none; border-radius: 50px; font-weight: 900; cursor: pointer; }

                .pt-pagination { display: flex; justify-content: center; margin-top: 64px; }
                .pt-loader-wrap { height: 50vh; display: flex; align-items: center; justify-content: center; }

                @media (max-width: 768px) {
                    .pt-container { padding: 0 16px; }
                    .pt-hero { padding: 120px 0 40px; }
                    .pt-title { font-size: 2.2rem; }
                    .pt-controls { flex-direction: column; align-items: stretch; gap: 12px; padding: 0; }
                    .pt-search-box input { height: 56px; font-size: 1rem; }
                    .pt-filter-box { height: 56px; width: 100%; justify-content: space-between; }
                    .pt-grid { grid-template-columns: 1fr; gap: 24px; }
                    .pt-card-top { height: 220px; }
                    .pt-card-body { padding: 24px; }
                }
            `}</style>
        </div>
    );
};

export default PlatformTeachers;
